/**
 * WP-06C-B — Inspector bound to one authoritative Loop read.
 *
 * The inspector never renders a Loop assembled from local memory. This wrapper
 * performs the authoritative detail read for the requested id and renders only
 * what the backend returned:
 *
 *   * `loading` shows a quiet placeholder — never stale or invented content;
 *   * `not_found` says so plainly, because the record is not visible to this
 *     account (deleted, or never in this workspace);
 *   * `unavailable` offers a retry and a categorical sentence, no provider text.
 */

import { Button } from "@/components/ui/button";
import { LoopInspector } from "./LoopInspector";
import { useHostedLoopDetail, useHostedLoops } from "@/lib/loops/product/hostedLoops";

export function HostedLoopInspector({
  loopId,
  onClose,
  variant = "panel",
  initialAction = null,
}: {
  loopId: string;
  onClose: () => void;
  variant?: "panel" | "sheet";
  initialAction?: "verify" | "evidence" | null;
}) {
  const detail = useHostedLoopDetail(loopId);
  const list = useHostedLoops();

  if (detail.phase === "loading") {
    return (
      <div className="flex h-full flex-col gap-3 bg-card p-5" aria-busy="true">
        <div className="h-4 w-24 animate-pulse rounded bg-muted" />
        <div className="h-5 w-3/4 animate-pulse rounded bg-muted" />
        <div className="h-24 w-full animate-pulse rounded-xl bg-muted" />
      </div>
    );
  }

  if (detail.phase !== "ready" || detail.loop === null) {
    return (
      <div className="flex h-full flex-col items-start gap-3 bg-card p-5">
        <p className="text-sm font-medium text-foreground">
          {detail.phase === "not_found"
            ? "This Loop is no longer available."
            : "Loops are briefly unavailable."}
        </p>
        {detail.message ? <p className="text-sm text-muted-foreground">{detail.message}</p> : null}
        <div className="flex gap-2">
          {detail.phase === "unavailable" ? (
            <Button size="sm" variant="outline" onClick={detail.refresh}>
              Try again
            </Button>
          ) : null}
          <Button size="sm" variant="ghost" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    );
  }

  return (
    <LoopInspector
      loop={detail.loop}
      allLoops={[...list.loops]}
      conditions={detail.conditions}
      onClose={onClose}
      variant={variant}
      initialAction={initialAction}
    />
  );
}
